// src/manager/pages/ManageUsers.tsx
import { useEffect, useState } from 'react';
import DOMPurify from 'dompurify';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { useManager } from '../hooks/useManager';
import ConfirmModal from '../../shared/components/ConfirmModal';

interface User {
  id?: string;
  login_id?: string;
  name: string;
  email?: string;
  phone?: string;
  real_credits: number;
  mode: string;
  suspended: boolean;
}

export default function ManageUsers() {
  const { fetchUsers, suspendUser, error, setError } = useManager();
  const [users, setUsers] = useState<User[]>([]);
  const [filteredUsers, setFilteredUsers] = useState<User[]>([]);
  const [search, setSearch] = useState('');
  const [sortColumn, setSortColumn] = useState<keyof User | null>(null);
  const [sortAsc, setSortAsc] = useState(true);
  const [modal, setModal] = useState<{ isOpen: boolean; message: string; callback: () => void } | null>(null);

  useEffect(() => {
    fetchUsers().then((data) => {
      setUsers(data);
    }).catch(() => {
      // Error handled by useManager
    });
  }, [fetchUsers]);

  useEffect(() => {
    setFilteredUsers(
      users.filter((user) => (user.login_id || user.name || '').toLowerCase().includes(search.toLowerCase()))
    );
  }, [search, users]);

  const handleSort = (column: keyof User) => {
    setSortColumn(column);
    setSortAsc(column === sortColumn ? !sortAsc : true);
    setFilteredUsers((prev) =>
      [...prev].sort((a, b) => {
        let aValue = a[column];
        let bValue = b[column];
        if (column === 'real_credits') {
          aValue = Number(aValue);
          bValue = Number(bValue);
        } else if (['suspended'].includes(column)) {
          aValue = aValue ? 1 : 0;
          bValue = bValue ? 1 : 0;
        } else {
          aValue = String(aValue).toLowerCase();
          bValue = String(bValue).toLowerCase();
        }
        return sortAsc ? (aValue > bValue ? 1 : -1) : (aValue < bValue ? 1 : -1);
      })
    );
  };

  const handleSuspend = async (userIdentifier: string, suspend: boolean) => {
    try {
      await suspendUser(userIdentifier, suspend);
      const data = await fetchUsers();
      setUsers(data);
      setError('');
      alert(`User ${suspend ? 'suspended' : 'unsuspended'}`);
    } catch (err: any) {
      setError(err.message || 'Failed to update suspend status');
    }
  };

  return (
    <div className="p-6">
      <h2 className="text-2xl font-semibold mb-4 text-gray-800">Manage Users</h2>
      {error && (
        <div className="mb-4 p-4 bg-red-100 text-red-700 rounded-lg">
          {error}
          <button
            onClick={() => setError('')}
            className="ml-4 text-red-700 underline"
            aria-label="Dismiss error"
          >
            Dismiss
          </button>
        </div>
      )}
      <div className="bg-white shadow rounded-lg p-4">
        <input
          type="text"
          placeholder="Search by login ID or player name..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full px-4 py-2 border border-gray-300 rounded-lg mb-4 focus:ring-2 focus:ring-purple-600"
        />
        <table className="w-full table-auto border-collapse">
          <thead>
            <tr className="bg-gray-200">
              {(['name', 'login_id', 'phone', 'real_credits', 'mode', 'suspended'] as (keyof User)[]).map((col) => (
                <th
                  key={col}
                  className="px-4 py-2 text-left cursor-pointer"
                  onClick={() => handleSort(col)}
                >
                  <div className="flex items-center">
                    {col === 'login_id' ? 'Login ID' : col === 'real_credits' ? 'Credits' : col.charAt(0).toUpperCase() + col.slice(1)}
                    {sortColumn === col && (sortAsc ? <ChevronUp size={16} /> : <ChevronDown size={16} />)}
                  </div>
                </th>
              ))}
              <th className="px-4 py-2 text-left">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredUsers.map((user, index) => (
              <tr key={index} className="border-t">
                <td className="px-4 py-2">{DOMPurify.sanitize(user.name || "")}</td>
                <td className="px-4 py-2 font-mono">{DOMPurify.sanitize(user.login_id || user.email || "—")}</td>
                <td className="px-4 py-2">{DOMPurify.sanitize(user.phone || "—")}</td>
                <td className="px-4 py-2">{DOMPurify.sanitize(user.real_credits.toString())}</td>
                <td className="px-4 py-2">{DOMPurify.sanitize(user.mode || "")}</td>
                <td className="px-4 py-2">{user.suspended ? 'Yes' : 'No'}</td>
                <td className="px-4 py-2">
                  <button
                    className={`px-3 py-1 rounded-lg ${user.suspended ? 'bg-green-600 hover:bg-green-700' : 'bg-yellow-600 hover:bg-yellow-700'} text-white`}
                    onClick={() => setModal({
                      isOpen: true,
                      message: `Are you sure you want to ${user.suspended ? 'unsuspend' : 'suspend'} user ${user.login_id || user.name}?`,
                      callback: () => handleSuspend(user.login_id || user.email || '', !user.suspended),
                    })}
                  >
                    {user.suspended ? 'Unsuspend' : 'Suspend'}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {modal && (
        <ConfirmModal
          isOpen={modal.isOpen}
          message={modal.message}
          onConfirm={() => {
            const callback = modal.callback;
            setModal(null);
            callback();
          }}
          onCancel={() => setModal(null)}
        />
      )}
    </div>
  );
}
